import { createFileRoute } from "@tanstack/react-router";
import { sendSentryEvent } from "@/lib/sentry-envelope";
import { deriveRouteTag } from "@/lib/sentry-route-tag";
import { resolveSentryRates } from "@/lib/sentry-config.functions";

// Dedicated endpoint to verify Sentry SSR telemetry.
//
// GET /debug/sentry-test sends a controlled error directly to Sentry's ingest
// endpoint (bypassing the wrapper throw path) so we can return the eventId
// Sentry will use to index the event. This is what lets you confirm reception
// in one round-trip — paste the eventId into Sentry's search bar and you
// should land on the exact issue.
//
// Expected tags on the resulting Sentry event:
//   route       -> /debug/sentry-test
//   release     -> SENTRY_RELEASE or CF_VERSION_METADATA_ID
//   environment -> SENTRY_ENVIRONMENT or NODE_ENV
//   runtime     -> server
//   ssr_label   -> debug-sentry-test

export const Route = createFileRoute("/debug/sentry-test")({
  server: {
    handlers: {
      GET: async ({ request }) => {
        const dsn = process.env.SENTRY_DSN;
        const url = new URL(request.url);
        const route = deriveRouteTag(url);
        const release =
          process.env.SENTRY_RELEASE ?? process.env.CF_VERSION_METADATA_ID ?? "dev";
        const environment =
          process.env.SENTRY_ENVIRONMENT ?? process.env.NODE_ENV ?? "production";

        if (!dsn) {
          return Response.json(
            {
              ok: false,
              reason: "SENTRY_DSN is not set on the Worker.",
              route,
              release,
              environment,
            },
            { status: 503 },
          );
        }

        const { incident } = resolveSentryRates();
        const error = new Error("SENTRY_TEST_CONTROLLED_ERROR");

        const result = await sendSentryEvent(dsn, error, {
          runtime: "server",
          route,
          release,
          environment,
          tags: {
            ssr_label: "debug-sentry-test",
            method: "GET",
            ...(incident ? { incident: "true" } : {}),
          },
          extra: { url: request.url },
        });

        return Response.json(
          {
            ok: result.accepted,
            eventId: result.eventId,
            status: result.status,
            responseBody: result.responseBody,
            route,
            release,
            environment,
            runtime: "server",
          },
          { status: result.accepted ? 200 : 502 },
        );
      },
    },
  },
  component: () => null,
});
